/**
 * Allows to TypeScript recognize google tag manager namespace
 */
import {IListItem} from "../../projects/list-page/interfaces/interfaces";
import { CartSteps } from '../../projects/shopping-cart/cart/enums/cart-steps';
import { ICartData } from '../../projects/shopping-cart/cart/interfaces';
import { getHiddenDataNumber, getHiddenDataString } from '../hidden-data-provider';
import { IOrder } from '../../projects/shopping-cart/cart-thank-you/interfaces';

declare global {
    interface Window {
        dataLayer: any;
    }
}

interface IGAProduct{
    name: string,
    id: string,
    price: number,
    category: string,
    variant: string,
    list: string,
}

interface IGAImpression {
    name: string,
    id: string,
    price: number,
    category: string,
    variant: string,
    list: string,
    position: number,
}

interface IActionField{
    list: string,
}

interface ICheckoutActionField {
    step: number,
    option?: number,
}

/**
 * send data to GTM after registration
 */
export const gtmCreateAccount = (referrer = 'from Signup form') => {
    if(!window.dataLayer) return;

    window.dataLayer.push({
        'GA_event_category': 'Registration',
        'GA_event_action': 'New account',
        'GA_event_label': referrer,
        'event': 'GTM event To GA'
    });
};

/**
 * send google analytics track event
 * @param {string} eventTitle - event title (data-gaevent="Click")
 * @param {string} eventValue - event value (data-gavalue="...")
 */
export const sendGAEvent = (eventTitle: string, eventValue: string) => {

    if(!window.dataLayer) return;

    window.dataLayer.push({
        'GA_event_action': eventTitle
        , 'GA_event_category': window.location.pathname
        , 'GA_event_label': eventValue
        , 'event': 'GTM event To GA'
    });
};

/**
 * get category from url
 */
export const getCategoryFromURL = (url: string) => {

    if(!url) return '';

    const idx = url.lastIndexOf('/');

    if(idx !== -1){
        return url.substring(0, url.lastIndexOf('/')).replace('/', '');
    }

    return '';
};

/**
 * send product clicks to the gtm
 */
export const sendGaEventImpressions = (impressions: IGAImpression[]) => {

    if(!window.dataLayer || !impressions || impressions.length <= 0) return;

    window.dataLayer.push({
        event: 'ProductImpression'
        ,ecommerce: {
            currencyCode: 'USD' //local currency is optional
            ,impressions: impressions
        }
    });
};

/**
 * send product details to the gtm
 */
export const sendGaEventProductDetails = (analyticsData: { price: number; name: string; id: string; category: string }[]) => {

    if(!window.dataLayer || !analyticsData || analyticsData.length <= 0) return;

    window.dataLayer.push({
        event: 'productDetail'
        ,ecommerce: {
            detail: {
                products: analyticsData
            }
        }
    });
};

/**
 * send product clicks to the gtm
 * actionField - {list: 'List Page'} (Product Page Tab / List Page / Shopping Cart Gallery / Homepage)
 */
export const sendGaEventProductClick = (actionField: IActionField, products: IGAProduct[]) => {

    if(!window.dataLayer || !products || products.length <= 0) return;

    window.dataLayer.push({
        event: 'productClick'
        ,ecommerce: {
            click: {
                actionField: actionField
                ,products: products
            }
        }
    });
};

/**
 * send Google Analytics list page event
 */
export const sendGAListPageEvent = (product: IListItem, listName: string = 'List Page') => {

    const title = product.title ? product.title : listName;

    const item = {
        name: product.documentName
        ,id: product.skuNumber
        ,price: product.skuPrice
        ,category: getCategoryFromURL(product.documentUrlPath)
        ,variant: 'NONE'
        ,list: title
    };

    sendGaEventProductClick({list: title}, [item]);
};

/**
 * send page type event to google analytics according to the following meta on the page:
 * <meta property="google-ecomm-pagetype" content="category">
 */
export const sendGAPageTypeEvent = () => {

    if(!window.dataLayer) return;

    const $meta = document.querySelector('meta[property="google-ecomm-pagetype"]');
    if(!$meta) return;

    window.dataLayer.push({
        pageType: $meta.getAttribute('content') || '',
    });
};

/**
 * send GA user guid event from the following hidden field:
 * <div id="ga-user-guid">@ViewBag.LayoutModel.GA_GUID</div>
 */
export const sendGAWavesUserGuidEvent = (gaGuid = '') => {
    if(!window.dataLayer) return;

    window['dataLayer'].push({
        'waves-user-guid': gaGuid || getHiddenDataString('ga-user-guid'),
    });
};

/**
 * This event is sent when user clicks on 'proceed to checkout' button on each cart step.
 */
export const sendGaCheckoutEvent = (cartStep: CartSteps, cartData: ICartData) => {

    if(!window.dataLayer) return;

    if(cartStep === CartSteps.Third){

        window.dataLayer.push({
            'GA_event_action': 'Payment Method'
            ,'GA_event_category': document.location.pathname
            ,'GA_event_label': 'Credit Card'
            ,'event': 'GTM event To GA'
        });
    }

    const actionField: ICheckoutActionField = {
        step: cartStep,
    };

    if(cartStep === CartSteps.First) {
        actionField.option = cartData.cartItems.reduce((total, cur) => total + cur.skuUnits, 0);
    }

    const products: any[] = cartData.cartItems.filter(cartItem => cartItem.isActive).map(cartItem => {
        return {
            skuid:	cartItem.skuid,
            skuDepartment:	cartItem.skuDepartmentID,
            skuDepartmentName:	cartItem.departmentName, //'plugins',
            skuguid: cartItem.skuguid,
            name: cartItem.skuName,
            skunumber:	cartItem.skuNumber,
            skuNeedsShipping:	cartItem.skuNeedsShipping,
            //isSBL:	false,
            //isPDL:	false,
            //img:	'/1lib/images/products/plugins/tiny/clarity-vx.png',
            thumb:	cartItem.skuThumb,
            quantity:	cartItem.skuUnits,
            price:	cartItem.priceData.price,
            upsell:	cartItem.priceData.discount,
            msrp:	cartItem.skU_MSRP,
            yousave: cartItem.priceData.discount,	//119.01,
            producttotal: cartItem.priceData.price * cartItem.skuUnits,
            link:	cartItem.documentUrlPath,
            cartComment: cartItem.cartComment,
            skuEnabled:	cartItem.skuEnabled,
            //mvrDiscountPrice:	0,
            //mvrItemDiscount:	0,
            pricega: cartItem.priceData.price,
        }
    })

    window['dataLayer'].push({
        event: 'checkout',
        CartValue: cartData.priceData.totalPrice,
        ecommerce: {
            checkout: {
                actionField: actionField,
                products,
            }
        }
    });
};

/**
 * This event is sent on 'thank you' page only after the real purchase.
 */
export const sendGATransactionTriggerEvent = (orderData: IOrder) => {

    if(!window.dataLayer) return;

    const products: any[] = [];

    for(let orderItem of orderData.orderItems){
        products.push({
            'name': orderItem.orderItemSKUName,
            'id': orderItem.skuNumber,
            'price': orderItem.discountPrice,
            'brand': 'Waves',
            'category': (orderItem.departmentDisplayName ?? '').toLowerCase(),
            'quantity': orderItem.orderItemUnitCount,
        });
    }

    if(orderData.upsellDiscount > 0){

        products.push({
            'name': 'Discount',
            'price': -orderData.upsellDiscount,
            'brand': 'Waves',
            'quantity': 1,
        });
    }

    window.dataLayer.push({
        event: 'TransactionTrigger'
        ,PaymentMethod: orderData.fundSourceType // 'Credit Card / PayPal / AliPay / Splitit'

        ,ecommerce: {
            purchase: {
                'actionField': {
                    'id': orderData.wavesReceiptID,
                    'affiliation': 'Online Store',
                    'revenue': orderData.totalPrice - orderData.taxPrice - orderData.shippingTaxPrice,
                    'tax': orderData.taxPrice + orderData.shippingTaxPrice,
                    'shipping': orderData.shippingPrice,
                },
                products,
            }
        }
    });

};

export const sendThankYouDataToGTM = (orderData: IOrder) => {
    if(!window.dataLayer) return;

    const quantity = orderData.orderItems.reduce(
        (previousQty, orderItem) => previousQty + orderItem.orderItemUnitCount,
        0
    );

    window.dataLayer.push({
        'quantity': quantity,
        'receiptid': orderData.wavesReceiptID,
        'priceWithoutTaxAndShipping': getHiddenDataNumber('price-without-tax-and-shipping'),
        'OrderID': orderData.orderID,
        'email': orderData.email,
        'yotpo-api-key': getHiddenDataString('yotpo-api-key'),
    });
};

/**
 * send data to gtm every time when product is added to cart
 */
export const sendGAEventAddToCart = (
    documentName: string,
    skuNumber: string,
    skuPrice: number,
    documentUrlPath: string,
    list: string = '',
) => {

    if(!window.dataLayer) return;

    const data = {
        event: 'addToCart'
        ,ecommerce: {
            currencyCode: 'USD'
            ,add: {
                products: [
                    {
                        name: documentName
                        ,id: skuNumber
                        ,price: skuPrice
                        ,variant: 'NONE'
                        ,quantity: 1
                        ,category: getCategoryFromURL(documentUrlPath)
                    }
                ],
            }
        }
    };

    if(list){
        // @ts-ignore
        data.ecommerce.add.actionField = { list, };
    }

    window.dataLayer.push(data);
};

/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-1143
 * Edited by Raz Liveyatan, 04.11.2021
 * Migrated by Raz Liveyatan, 27.06.2022
 * send page view to the gtm
 * @param {any} PageViewObject
 */
export const sendGaPageView = (PageViewObject:any) => {

    if(!window.dataLayer || !PageViewObject) return;

    window.dataLayer.push({
        event: 'pageview',
        page: {
            path: PageViewObject.pageLocation,
            title: PageViewObject.title
        }
    });
};

